<template>
  <Teleport to="body">
    <Transition name="p-panel-backdrop">
      <div
        v-if="modelValue"
        class="p-slide-panel__backdrop"
        @click="handleBackdropClick"
      ></div>
    </Transition>

    <Transition :name="position === 'start' ? 'p-panel-slide-l' : 'p-panel-slide-r'">
      <aside
        v-if="modelValue"
        :class="['p-slide-panel', `p-slide-panel--${position}`]"
        :style="{ width: panelWidth }"
        role="dialog"
        aria-modal="true"
      >
        <header class="p-slide-panel__head">
          <div class="p-slide-panel__title">
            <slot name="header">
              <h5 class="p-slide-panel__title-text">{{ title }}</h5>
            </slot>
          </div>
          <button
            type="button"
            class="p-slide-panel__close"
            aria-label="Close"
            @click="handleClose"
          >
            <i class="material-icons-round">close</i>
          </button>
        </header>

        <div class="p-slide-panel__body">
          <slot></slot>
        </div>

        <footer v-if="$slots.footer" class="p-slide-panel__foot">
          <slot name="footer"></slot>
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<script>
import { computed, watch, onMounted, onUnmounted } from 'vue'

export default {
  name: 'SlidePanel',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    width: { type: String, default: '60%' },
    position: {
      type: String,
      default: 'end',
      validator: (v) => ['end', 'start'].includes(v),
    },
    closeOnBackdrop: { type: Boolean, default: false },
    closeOnEscape: { type: Boolean, default: true },
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { emit }) {
    const panelWidth = computed(() => props.width)

    const handleClose = () => {
      emit('update:modelValue', false)
      emit('close')
    }

    const handleBackdropClick = () => {
      if (props.closeOnBackdrop) handleClose()
    }

    const handleKeydown = (e) => {
      if (e.key === 'Escape' && props.closeOnEscape && props.modelValue) handleClose()
    }

    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) document.body.style.overflow = 'hidden'
      else document.body.style.overflow = ''
    })

    onMounted(() => document.addEventListener('keydown', handleKeydown))
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    })

    return { panelWidth, handleBackdropClick, handleClose }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/pineapple";

// 패널은 모달보다 한 단계 아래 ($p-z-panel = 10000, modal = 11000)
.p-slide-panel__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(26, 22, 18, 0.45);
  z-index: $p-z-panel;
}

.p-slide-panel {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: $p-z-panel + 5;
  display: flex;
  flex-direction: column;
  background: $p-panel;
  font-family: $p-font-body;
  color: $p-ink;
  outline: 0;

  &--end {
    right: 0;
    box-shadow: -8px 0 32px -4px rgba(26, 22, 18, 0.20);
  }

  &--start {
    left: 0;
    box-shadow: 8px 0 32px -4px rgba(26, 22, 18, 0.20);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid $p-line;
    background: $p-panel;
    flex-shrink: 0;
  }

  &__title {
    flex: 1;
    min-width: 0;
  }

  &__title-text {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: -0.015em;
    color: $p-ink;
  }

  &__close {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: 0;
    background: transparent;
    color: $p-ink-soft;
    display: inline-grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;
    flex-shrink: 0;

    > i { font-size: 18px; }

    &:hover {
      background: $p-panel-alt;
      color: $p-ink;
    }
  }

  &__body {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    overflow-x: hidden;
    background: $p-bg;
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid $p-line;
    background: $p-panel-alt;
    flex-shrink: 0;

    > button,
    .a-btn {
      height: 36px;
      line-height: 1;
      box-sizing: border-box;
    }
  }

  @media (max-width: 992px) {
    min-width: 80%;
  }

  @media (max-width: 768px) {
    width: 100% !important;
  }
}

// ── Transitions ──
.p-panel-backdrop-enter-active,
.p-panel-backdrop-leave-active {
  transition: opacity 0.22s $p-ease;
}
.p-panel-backdrop-enter-from,
.p-panel-backdrop-leave-to {
  opacity: 0;
}

.p-panel-slide-r-enter-active,
.p-panel-slide-r-leave-active,
.p-panel-slide-l-enter-active,
.p-panel-slide-l-leave-active {
  transition: transform 0.28s $p-ease;
}
.p-panel-slide-r-enter-from,
.p-panel-slide-r-leave-to {
  transform: translateX(100%);
}
.p-panel-slide-l-enter-from,
.p-panel-slide-l-leave-to {
  transform: translateX(-100%);
}
</style>
